import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { GraduationCap, Calendar, Banknote, Phone, Mail, CheckCircle2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1762967020958-6b2118ef93b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjaHVyY2glMjBhcmNoaXRlY3R1cmUlMjBzdW5saWdodHxlbnwwfHx8fDE3NzY1MzkzNjR8MA&ixlib=rb-4.1.0&q=85";

export default function BibleSchool() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", id_number: "",
    tenure: "", previous_education: "", reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.tenure) return toast.error("Please choose a tenure");
    setLoading(true);
    try {
      await api.post("/bible-school/register", f);
      setDone(true);
      toast.success("Registration received. We'll be in touch!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="bible-school-page">
      {/* HERO */}
      <section className="relative bg-olive text-bone overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={HERO} alt="EBI" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 bg-terracotta text-white px-4 py-2 overline">
              <GraduationCap size={14} /> EBI · Mbombela
            </div>
            <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter leading-[0.95] mt-6">
              El-Shaddai <em className="italic font-light text-terracotta">Bible</em> Institute
            </h1>
            <p className="mt-6 text-bone/85 text-lg max-w-2xl leading-relaxed">
              Empowering your spiritual growth and biblical understanding. Grow in <span className="text-terracotta font-semibold">grace, truth, and purpose</span>.
            </p>
            <p className="mt-6 inline-flex items-center gap-2 bg-bone/10 border border-bone/20 px-5 py-3 text-sm">
              <Calendar size={16} className="text-terracotta" />
              <span className="font-semibold">2026 Admissions Open</span>
              <span className="text-bone/70">· 12 January – 07 February 2026</span>
            </p>
          </div>
        </div>
      </section>

      {/* FEES & TENURE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <p className="overline text-terracotta mb-3">Fees & Tenure</p>
        <h2 className="font-heading font-black text-4xl tracking-tighter text-olive mb-10">Pick a tenure that fits your journey.</h2>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white border border-border p-8">
            <Banknote size={22} className="text-olive" />
            <p className="overline text-terracotta mt-6">Registration</p>
            <p className="font-heading font-black text-5xl text-ink mt-2">R700</p>
            <p className="text-sm text-muted_ink mt-2">One-time registration fee</p>
          </div>
          <div className="bg-white border border-border p-8 hover:border-olive transition-colors">
            <CheckCircle2 size={22} className="text-olive" />
            <p className="overline text-terracotta mt-6">10-Month Tenure</p>
            <p className="font-heading font-black text-5xl text-ink mt-2">R800<span className="text-lg text-muted_ink font-normal">/mo</span></p>
            <p className="text-sm text-muted_ink mt-2">Paced study · full curriculum</p>
          </div>
          <div className="bg-terracotta text-white border border-terracotta p-8 hover:bg-terracotta-hover transition-colors">
            <CheckCircle2 size={22} className="text-white" />
            <p className="overline text-white/85 mt-6">6-Month Tenure</p>
            <p className="font-heading font-black text-5xl mt-2">R1000<span className="text-lg font-normal text-white/75">/mo</span></p>
            <p className="text-sm text-white/85 mt-2">Intensive · same curriculum, faster</p>
          </div>
        </div>

        <p className="mt-6 text-sm italic text-muted_ink">Classes are filling fast — enroll today.</p>
      </section>

      {/* FORM + CONTACT */}
      <section className="bg-cream border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="overline text-terracotta mb-3">Contact EBI</p>
            <h3 className="font-heading font-black text-3xl text-olive tracking-tight">Prefer to speak to us first?</h3>
            <p className="mt-4 text-ink/75">Reach our admissions team — we're happy to answer any questions.</p>

            <div className="mt-8 space-y-4">
              <a href="tel:0661255060" className="flex items-center gap-3 text-ink hover:text-terracotta">
                <div className="w-10 h-10 bg-white border border-border flex items-center justify-center"><Phone size={16} className="text-olive" /></div>
                <div>
                  <p className="text-xs text-muted_ink">Ms. Yolanda</p>
                  <p className="font-semibold">066 125 5060</p>
                </div>
              </a>
              <a href="tel:0609393746" className="flex items-center gap-3 text-ink hover:text-terracotta">
                <div className="w-10 h-10 bg-white border border-border flex items-center justify-center"><Phone size={16} className="text-olive" /></div>
                <div>
                  <p className="text-xs text-muted_ink">Mr. Modise</p>
                  <p className="font-semibold">060 939 3746</p>
                </div>
              </a>
              <a href="mailto:elshaddainst@gmail.com" className="flex items-center gap-3 text-ink hover:text-terracotta">
                <div className="w-10 h-10 bg-white border border-border flex items-center justify-center"><Mail size={16} className="text-olive" /></div>
                <div>
                  <p className="text-xs text-muted_ink">Email</p>
                  <p className="font-semibold">elshaddainst@gmail.com</p>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="bg-white border border-border p-12 text-center" data-testid="ebi-success">
                <CheckCircle2 size={44} className="text-terracotta mx-auto" />
                <p className="overline text-terracotta mt-4">Registration Received</p>
                <h3 className="font-heading font-black text-3xl text-olive mt-2">You're enrolled, {f.full_name.split(" ")[0]}!</h3>
                <p className="text-ink/75 mt-4">Our admissions team will contact you within 48 hours with next steps.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-border p-8 md:p-10 space-y-5">
                <p className="overline text-terracotta">Register for EBI 2026</p>
                <h3 className="font-heading font-black text-3xl text-olive tracking-tight">Start your application</h3>

                <div className="grid md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label>Full name *</Label>
                    <Input required value={f.full_name} onChange={set("full_name")} data-testid="ebi-name" className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" required value={f.email} onChange={set("email")} data-testid="ebi-email" className="mt-2 h-11" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Phone *</Label>
                    <Input required value={f.phone} onChange={set("phone")} data-testid="ebi-phone" className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label>ID number</Label>
                    <Input value={f.id_number} onChange={set("id_number")} data-testid="ebi-id" className="mt-2 h-11" />
                  </div>
                </div>
                <div>
                  <Label>Tenure *</Label>
                  <Select value={f.tenure} onValueChange={set("tenure")}>
                    <SelectTrigger data-testid="ebi-tenure" className="mt-2 h-11">
                      <SelectValue placeholder="Select tenure" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10-month">10-Month Tenure (R800/month)</SelectItem>
                      <SelectItem value="6-month">6-Month Tenure (R1000/month)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Previous education</Label>
                  <Input value={f.previous_education} onChange={set("previous_education")} data-testid="ebi-prev" className="mt-2 h-11" />
                </div>
                <div>
                  <Label>Why EBI?</Label>
                  <Textarea rows={3} value={f.reason} onChange={set("reason")} data-testid="ebi-reason" className="mt-2" />
                </div>
                <Button type="submit" disabled={loading} data-testid="ebi-submit"
                  className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none h-12 px-10 text-base font-semibold">
                  {loading ? "Submitting…" : "Submit Registration"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
